import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Table, Tag, Alert } from 'antd';
import { TeamOutlined, FileTextOutlined, ToolOutlined, AlertOutlined, DollarOutlined, ClockCircleOutlined, WarningOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { usePermissao } from '../hooks/usePermissao';

const { Title, Text } = Typography;

const fmtMoeda = (v) => v ? 'R$ ' + parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : 'R$ 0,00';
const fmtData = (v) => v ? new Date(v).toLocaleDateString('pt-BR') : '-';

export default function Dashboard() {
  const [dados, setDados] = useState(null);
  const [laudosAlerta, setLaudosAlerta] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { perfil } = usePermissao();

  const podeVerDashboard = ['admin', 'engenheiro', 'gerente'].includes(perfil);

  useEffect(() => { if (podeVerDashboard) carregar(); else setLoading(false); }, []);

  const carregar = async () => {
    try {
      const [rDash, rLaudos] = await Promise.all([
        api.get('/gestao/dashboard'),
        api.get('/gestao/laudos'),
      ]);
      setDados(rDash.data);
      setLaudosAlerta([...rLaudos.data.vencidos.slice(0, 3), ...rLaudos.data.vencendo30.slice(0, 3)]);
    } catch {}
    finally { setLoading(false); }
  };

  if (!podeVerDashboard) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <ToolOutlined style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
      <Title level={4} style={{ color: '#888' }}>Bem-vindo ao Celum ERP!</Title>
      <Text type="secondary">Use o menu lateral para acessar as funcionalidades.</Text>
    </div>
  );

  const colunasAlerta = [
    { title: 'Código', dataIndex: 'codigo', width: 110 },
    { title: 'TAG', dataIndex: 'tag', width: 80 },
    { title: 'Cliente', render: (_, r) => r.razao_faturamento || r.razao_social || r.nome_cliente },
    { title: 'Vencimento', dataIndex: 'proxima_inspecao_externa', render: v => {
      const venc = new Date(v);
      const hoje = new Date();
      const dias = Math.ceil((venc - hoje) / (1000 * 60 * 60 * 24));
      return <span style={{ color: dias < 0 ? '#cf1322' : dias <= 30 ? '#fa8c16' : '#faad14', fontWeight: 'bold' }}>{fmtData(v)}{dias < 0 ? ' (VENCIDO)' : ` (${dias}d)`}</span>;
    }},
    { title: '', render: (_, r) => <a onClick={() => navigate('/clientes/' + r.cliente_id)} style={{ fontSize: 12 }}>Ver cliente</a>, width: 80 },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Dashboard</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/clientes')}>
            <Statistic title="Clientes Ativos" value={dados?.clientes_ativos || 0} prefix={<TeamOutlined style={{ color: '#0099FF' }} />} valueStyle={{ color: '#0099FF' }} loading={loading} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/propostas')}>
            <Statistic title={"Propostas Servi\u00e7o (abertas)"} value={dados?.propostas_abertas || 0} prefix={<FileTextOutlined style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a' }} loading={loading} />
            <Text type="secondary" style={{ fontSize: 12 }}>{fmtMoeda(dados?.valor_propostas_abertas)} em aberto</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/propostas')}>
            <Statistic title="Propostas Produto (abertas)" value={dados?.propostas_produto_abertas || 0} prefix={<ShoppingOutlined style={{ color: '#2f54eb' }} />} valueStyle={{ color: '#2f54eb' }} loading={loading} />
            <Text type="secondary" style={{ fontSize: 12 }}>{fmtMoeda(dados?.valor_propostas_produto_abertas)} em aberto</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/laudos')}>
            <Statistic title="Laudos Emitidos (30d)" value={dados?.laudos_30d || 0} prefix={<ToolOutlined style={{ color: '#722ed1' }} />} valueStyle={{ color: '#722ed1' }} loading={loading} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/gestao')}>
            <Statistic title="Laudos Vencendo (90d)" value={dados?.laudos_vencendo || 0} prefix={<AlertOutlined style={{ color: '#ff4d4f' }} />} valueStyle={{ color: '#ff4d4f' }} loading={loading} />
            {dados?.laudos_vencidos > 0 && <Text type="danger" style={{ fontSize: 12 }}>{"\u26a0\ufe0f " + dados.laudos_vencidos + " j\u00e1 vencidos!"}</Text>}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title={"Faturamento Servi\u00e7o (m\u00eas)"} value={dados?.faturamento_mes || 0} prefix={<DollarOutlined style={{ color: '#13c2c2' }} />} valueStyle={{ color: '#13c2c2' }} formatter={v => fmtMoeda(v)} loading={loading} />
            <Text type="secondary" style={{ fontSize: 12 }}>{dados?.nfs_mes || 0} NFS-e emitidas</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title={"Faturamento Produto (m\u00eas)"} value={dados?.faturamento_produto_mes || 0} prefix={<DollarOutlined style={{ color: '#2f54eb' }} />} valueStyle={{ color: '#2f54eb' }} formatter={v => fmtMoeda(v)} loading={loading} />
            <Text type="secondary" style={{ fontSize: 12 }}>{dados?.nfe_mes || 0} NF-e autorizadas</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title={"NFs Emitidas (m\u00eas)"} value={(dados?.nfs_mes || 0) + (dados?.nfe_mes || 0)} prefix={<FileTextOutlined style={{ color: '#fa8c16' }} />} valueStyle={{ color: '#fa8c16' }} loading={loading} />
            <Text type="secondary" style={{ fontSize: 12 }}>{dados?.nfs_mes || 0} NFS-e - {dados?.nfe_mes || 0} NF-e</Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Agendamentos (semana)" value={dados?.agendamentos_semana || 0} prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />} valueStyle={{ color: '#1890ff' }} loading={loading} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Leads Cadastrados" value={dados?.leads_total || 0} prefix={<TeamOutlined style={{ color: '#eb2f96' }} />} valueStyle={{ color: '#eb2f96' }} loading={loading} />
          </Card>
        </Col>
      </Row>

      {laudosAlerta.length > 0 && (
        <Card size="small" title={<span><WarningOutlined style={{ color: '#fa8c16' }} /> Laudos que precisam de atenção</span>} extra={<a onClick={() => navigate('/gestao')}>Ver todos</a>}>
          <Table columns={colunasAlerta} dataSource={laudosAlerta} rowKey="id" size="small" pagination={false} />
        </Card>
      )}
    </div>
  );
}
